import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, AlertCircle } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang, route, postPath } from "../i18n";
import { useCompany, useSitePosts } from "../components/SiteContext";
import SiteImage from "../components/SiteImage";
import { CATEGORIES } from "../data/posts";
import { Reveal } from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import FinalCta from "../components/FinalCta";
import NotFound from "./NotFound";
import { track } from "../lib/track";

const fmt = (iso, lang) =>
  new Date(iso + "T12:00:00").toLocaleDateString(lang === "es" ? "es-US" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const BlogPost = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const company = useCompany();
  const allPosts = useSitePosts();
  const post = allPosts.find((p) => p.slug === slug);
  if (!post) return <NotFound />;

  const c = post[lang] || post.en;
  const cat = CATEGORIES[post.category]?.[lang] || post.category;
  const isDraft = post.status !== "published";
  const related = allPosts
    .filter((p) => p.slug !== slug && p.category === post.category)
    .concat(allPosts.filter((p) => p.slug !== slug && p.category !== post.category))
    .slice(0, 2);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: c.title,
    description: c.excerpt,
    inLanguage: lang,
    datePublished: post.date,
    dateModified: post.updated,
    author: {
      "@type": "Person",
      name: post.author?.name || company.founder,
      jobTitle: post.author?.role?.[lang] || company.founderRole[lang],
      worksFor: { "@type": "Organization", name: company.name },
    },
    publisher: { "@type": "Organization", name: company.name },
  };

  return (
    <>
      <Seo title={`${c.title} | Aviah Insights`} siteName={company.name} description={c.excerpt} type="article" jsonLd={jsonLd} />

      <article className="bg-[#F4F0E8]">
        <header className="mx-auto max-w-4xl px-5 pb-14 pt-32 sm:px-8 sm:pt-44">
          <Reveal>
            <Link
              to={route(lang, "blog")}
              className="inline-flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.18em] text-[#171717]/50 transition-colors hover:text-[#8a6f3e]"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t.common.backToBlog}
            </Link>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="eyebrow text-[#8a6f3e]">{cat}</span>
              {isDraft && (
                <span className="border border-[#C6A46A]/60 px-2 py-0.5 text-[0.55rem] uppercase tracking-[0.15em] text-[#8a6f3e]">
                  {t.common.draft}
                </span>
              )}
            </div>
            <h1 className="mt-6 text-3xl font-light leading-[1.12] tracking-tight text-[#171717] sm:text-4xl lg:text-5xl">
              {c.title}
            </h1>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 border-t border-[#171717]/12 pt-6 text-[0.65rem] uppercase tracking-[0.15em] text-[#171717]/45">
              <span>
                {t.common.by} <Link to={route(lang, "author")} className="text-[#8a6f3e] hover:underline">{post.author?.name || company.founder}</Link>
              </span>
              <span>{t.common.published}: {fmt(post.date, lang)}</span>
              <span>{t.common.reviewed}: {fmt(post.reviewed, lang)}</span>
              <span>{post.reading} {t.common.minRead}</span>
            </div>
          </Reveal>
        </header>

        <Reveal delay={0.1}>
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <SiteImage
              slot={post.img}
              alt={c.title}
              className="border border-[#171717]/10"
              imgClassName="aspect-[16/7] w-full object-cover"
            />
          </div>
        </Reveal>

        <div className="border-t border-[#171717]/10">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-[240px_1fr]">
            {!isDraft && c.content?.length > 0 && (
              <aside className="hidden lg:block">
                <div className="sticky top-28">
                  <p className="eyebrow text-[#8a6f3e]">{t.common.toc}</p>
                  <ul className="mt-5 space-y-3">
                    {c.content.map((sec, i) => (
                      <li key={i}>
                        <a href={`#sec-${i}`} className="text-xs leading-relaxed text-[#171717]/55 transition-colors hover:text-[#8a6f3e]">
                          {sec.h}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>
            )}

            <div className="max-w-2xl">
              <Reveal>
                <p className="text-lg font-light leading-relaxed text-[#171717]">{c.excerpt}</p>
              </Reveal>

              {isDraft ? (
                <div className="mt-10" data-testid="draft-outline">
                  <p className="text-[0.95rem] leading-relaxed text-[#171717]/70">
                    {lang === "es"
                      ? "Este artículo está en revisión editorial. Este es el mapa que seguirá:"
                      : "This article is in editorial review. This is the map it will follow:"}
                  </p>
                  <ol className="mt-6 space-y-3">
                    {(c.outline || []).map((item, i) => (
                      <li key={i} className="flex items-start gap-4 border-b border-[#171717]/10 pb-3 text-sm text-[#171717]/75">
                        <span className="text-[#8a6f3e]">{String(i + 1).padStart(2, "0")}</span>
                        {item}
                      </li>
                    ))}
                  </ol>
                </div>
              ) : (
                <div className="mt-10 space-y-12" data-testid="article-body">
                  {(c.content || []).map((sec, i) => (
                    <section key={i} id={`sec-${i}`}>
                      <h2 className="text-xl font-light tracking-tight text-[#171717] sm:text-2xl">{sec.h}</h2>
                      {(sec.p || []).map((para, j) => (
                        <p key={j} className="mt-4 text-[0.95rem] leading-relaxed text-[#171717]/75 sm:text-base">
                          {para}
                        </p>
                      ))}
                    </section>
                  ))}
                </div>
              )}

              {!isDraft && c.faqs && c.faqs.length > 0 && (
                <div className="mt-16">
                  <FaqAccordion items={c.faqs} tone="light" testid="article-faq" />
                </div>
              )}

              <div className="mt-14 flex items-start gap-4 border border-[#C6A46A]/40 bg-[#EEE9DF] p-6" data-testid="article-disclaimer">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#8a6f3e]" strokeWidth={1.5} />
                <p className="text-xs leading-relaxed text-[#171717]/70">{t.footer.miniDisclaimer}</p>
              </div>

              <div className="mt-10 border border-[#171717]/12 p-6" data-testid="author-box">
                <p className="eyebrow text-[#8a6f3e]">{t.common.by} {post.author?.name || company.founder}</p>
                <p className="mt-2 text-[0.7rem] uppercase tracking-[0.15em] text-[#171717]/50">{post.author?.role?.[lang] || company.founderRole[lang]}</p>
                <p className="mt-4 text-xs leading-relaxed text-[#171717]/70">{post.author?.bio?.[lang]}</p>
              </div>

              <div className="mt-14">
                <p className="eyebrow text-[#8a6f3e]">{t.common.related}</p>
                <div className="mt-6 space-y-4">
                  {related.map((p) => (
                    <Link
                      key={p.slug}
                      to={postPath(lang, p.slug)}
                      onClick={() => track("blog_read", { slug: p.slug, from: slug, path: postPath(lang, p.slug) })}
                      className="group flex items-center justify-between gap-4 border border-[#171717]/12 p-5 transition-colors hover:border-[#C6A46A]"
                    >
                      <div>
                        <span className="text-[0.6rem] uppercase tracking-[0.18em] text-[#8a6f3e]">
                          {CATEGORIES[p.category]?.[lang] || p.category}
                        </span>
                        <p className="mt-1 text-sm font-normal text-[#171717]">{p[lang]?.title || p.en?.title}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-[#C6A46A] transition-transform group-hover:translate-x-1" />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <FinalCta />
    </>
  );
};

export default BlogPost;
